/**
 * FRONT-2884 / FRONT-3301: the Builder's auto-handover menu, shared by the
 * "…" menu and the settings panel so both read and write a session the same
 * way. Pure — unit tested.
 *
 * The levels come first and Off last: Off is the exception, not a level.
 * The level a session uses without a pick of its own (the organisation
 * default, else EDDY's) carries a "default" marker, and choosing that entry
 * clears the session's pick, so it follows the default again — including a
 * later admin change.
 */
import type { CodingSessionView } from './coding-events';

export const AUTO_CLEAR_LEVELS = ['20', '30', '40', '50', '60', '70'] as const;
export const AUTO_CLEAR_CHOICES = [...AUTO_CLEAR_LEVELS, 'off'] as const;

type AutoClearSession = Pick<
  CodingSessionView,
  'autoClearEnabled' | 'autoClearThresholdPercent' | 'autoClearThresholdSource' | 'autoClearDefaultPercent'
>;

/** The menu's current choice for a session: 'off' or the level in force. */
export function autoClearChoiceOf(session: AutoClearSession | null | undefined): string {
  if (!session) return 'off';
  return session.autoClearEnabled ? String(session.autoClearThresholdPercent) : 'off';
}

/** True for the level the session falls back to without a pick of its own. */
export function isDefaultAutoClearChoice(choice: string, session: AutoClearSession | null | undefined): boolean {
  return !!session && choice !== 'off' && choice === String(session.autoClearDefaultPercent);
}

/** Menu label for a level or Off. */
export function autoClearChoiceLabel(choice: string): string {
  return choice === 'off' ? 'Off' : `At ${choice}% context`;
}

export type AutoClearRequest = { enabled: false } | { enabled: true; thresholdPercent: number | null };

/**
 * What picking `choice` sends, or null when it changes nothing. The default
 * entry sends `thresholdPercent: null` (follow the default); any other level
 * sends its number. Picking the default entry while the session holds that
 * same number as its OWN pick is not a no-op: it hands the session back to
 * the default, so a later admin change reaches it.
 */
export function autoClearRequestFor(
  choice: string,
  current: string,
  session: AutoClearSession,
): AutoClearRequest | null {
  if (choice === 'off') return current === 'off' ? null : { enabled: false };
  const isDefault = isDefaultAutoClearChoice(choice, session);
  if (choice === current && !(isDefault && session.autoClearThresholdSource === 'session')) return null;
  return { enabled: true, thresholdPercent: isDefault ? null : Number(choice) };
}
